import type { CreatePointToPointRequest, PointToPoint, Ratebook } from '@api/useFreight/types'
import { type Destination, type FilteredMetadata, type Origin, useRateManagement } from '@api/useFreight/useFreight'
import { useQueryClient } from '@tanstack/react-query'
import { Form, message } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import type { LocationOption, PointToPointFormValues } from '../types'
import { useLocalStorage } from '../utils'

/**
 * Custom hook for managing point-to-point creation and editing form
 * @param ratebook The current ratebook
 * @param onSuccess Callback when operation succeeds
 * @param onClose Callback to close drawer
 * @returns Form state and handlers
 */
export function usePointToPointForm({
  ratebook,
  ratebookOptions,
  onSuccess,
  onClose,
}: {
  ratebook: Ratebook
  ratebookOptions?: FilteredMetadata
  onSuccess: (created: PointToPoint) => void
  onClose: () => void
}) {
  const [form] = Form.useForm<PointToPointFormValues>()
  const queryClient = useQueryClient()
  const { useCreatePointToPointNew } = useRateManagement()
  const createMutation = useCreatePointToPointNew()

  const [selectedUnit, setSelectedUnit] = useState<string>('gallons')
  const [showDistance, setShowDistance] = useState(false)
  const [hasEndDate, setHasEndDate] = useLocalStorage('point-to-point-has-end-date', false)
  const formInitializedRef = useRef(false)
  const originSelectRef = useRef<unknown>(null)

  // Unit value from form
  const unitValue = Form.useWatch('unit', form)

  // Update selected unit when form value changes
  useEffect(() => {
    if (unitValue) {
      setSelectedUnit(unitValue)
    }
  }, [unitValue])

  /**
   * Initialize form for editing or creating
   * @param visible Whether the drawer is visible
   * @param editingPointToPoint Point-to-point being edited (if any)
   */
  const initializeForm = (visible: boolean, editingPointToPoint?: PointToPoint | null) => {
    // Reset initialization flag if drawer is closed
    if (!visible) {
      formInitializedRef.current = false
      return
    }

    // Skip if form is already initialized
    if (formInitializedRef.current) {
      return
    }

    if (visible && editingPointToPoint) {
      // Edit mode
      const currentRate = editingPointToPoint.rates?.[0] || {}

      // Set distance visibility
      setShowDistance(!!currentRate.distance)

      // Set form values
      form.setFieldsValue({
        origin: editingPointToPoint.origin || undefined,
        destination: editingPointToPoint.destination || undefined,
        product_group: editingPointToPoint.product_group || undefined,
        unit: editingPointToPoint.unit || 'gallons',
        rate: currentRate.rate || 0,
        distance: currentRate.distance || undefined,
        use_surcharge: editingPointToPoint.use_surcharge ? 'Yes' : 'No',
        effective_from: currentRate.effective_from ? dayjs(currentRate.effective_from) : dayjs(),
        effective_to: currentRate.effective_to ? dayjs(currentRate.effective_to) : undefined,
      })

      // Set end date flag
      setHasEndDate(!!currentRate.effective_to)
    } else if (visible) {
      // Create mode
      form.resetFields()
      form.setFieldsValue({
        use_surcharge: 'Yes',
        effective_from: dayjs(),
        unit: 'gallons',
        rate: 0.0,
      })
      setShowDistance(false)
    }

    // Mark form as initialized
    formInitializedRef.current = true
  }

  /**
   * Handle unit change
   * @param value New unit value
   */
  const handleUnitChange = (value: string) => {
    // Get current form values
    const currentValues = form.getFieldsValue()

    // Calculate new rate based on unit type
    let newRate = currentValues.rate || 0
    if (value === 'gallons' || value === 'liters') {
      newRate = Number.parseFloat(Number(newRate).toFixed(2))
    } else {
      newRate = Math.round(newRate)
    }

    // Update form with new values
    form.setFieldsValue({
      ...currentValues,
      unit: value,
      rate: newRate,
    })
  }

  /**
   * Submit form
   */
  const handleSubmit = async (editingPointToPoint?: PointToPoint | null) => {
    const values = await form.validateFields()

    // Validate rate
    if (values.rate <= 0) {
      message.error('Rate must be greater than 0')
      return
    }

    // Look for origin and destination IDs
    const selectedOrigin =
      ratebookOptions?.origins?.find((o: Origin) => o.name === values.origin) ||
      ratebookOptions?.destinations?.find((d: Destination) => d.name === values.origin)

    const selectedDestination =
      ratebookOptions?.destinations?.find((d: Destination) => d.name === values.destination) ||
      ratebookOptions?.origins?.find((o: Origin) => o.name === values.destination)

    // Validate origin and destination
    if (!selectedOrigin?.id || !selectedDestination?.id) {
      message.error('Could not find valid IDs for origin or destination. Please try different selections.')

      return
    }

    // Set distance value
    const distance = showDistance && values.distance ? Number(values.distance) : null

    // Prepare data
    const formattedData = {
      ...(editingPointToPoint ? { _id: editingPointToPoint._id } : {}),
      counterparty: ratebook.counterparty_name,
      counterparty_id: ratebook.counterparty_id,
      book_type: ratebook.book_type,
      origin: values.origin,
      origin_id: selectedOrigin.id,
      destination: values.destination,
      destination_id: selectedDestination.id,
      unit: values.unit,
      product_group: values.product_group,
      use_surcharge: values.use_surcharge === 'Yes',
      disabled: editingPointToPoint ? editingPointToPoint.disabled : false,
      rates: [
        {
          effective_from: values.effective_from.format('YYYY-MM-DD'),
          effective_to: values.effective_to?.format('YYYY-MM-DD'),
          rate: Number(values.rate),
          distance: distance,
          created_by: localStorage.getItem('user_email') || 'system',
          created_on: dayjs().utc().toISOString(),
        },
      ],
      rate_history: editingPointToPoint?.rate_history || [],
      updated_on: dayjs().utc().toISOString(),
      updated_by: localStorage.getItem('user_email') || 'system',
    }

    // Submit data
    const created = await createMutation.mutateAsync(formattedData as CreatePointToPointRequest)

    // Update query cache
    await queryClient.invalidateQueries({ queryKey: ['ratebook/p2p'] })

    // Show success message
    message.success(`Point to point rate ${editingPointToPoint ? 'updated' : 'created'} successfully`)

    // Reset form and close drawer
    form.resetFields()
    onSuccess(created)
    onClose()
  }

  /**
   * Handle drawer close
   */
  const handleClose = () => {
    formInitializedRef.current = false
    onClose()
  }

  return {
    form,
    showDistance,
    setShowDistance,
    hasEndDate,
    setHasEndDate,
    selectedUnit,
    submitting: createMutation.isPending,
    originSelectRef,
    initializeForm,
    handleUnitChange,
    handleSubmit,
    handleClose,
  }
}
